import React, { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import styled from 'styled-components';
import { Avatar, Button, H3, H4, Switch, Text, Tooltip } from 'components';
import { theme, toRem } from 'design';
import { formatJobTitle } from 'utils';
import { IEmployee, IEmployeeSite, SiteRole } from '../../../types';

interface ITipAcceptanceEmployeeProps {
  employee?: IEmployee;
  currentRole?: SiteRole;
  siteId?: string;
  isLoading?: boolean;
  isUpdateLoading?: boolean;
  onChangeAcceptance?(acceptance: boolean): void;
  onViewCode?(): void;
}

const Layout = styled.section`
  position: relative;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  height: auto;
  margin-bottom: ${toRem(18)};
  padding: 0 ${toRem(theme.layout.gutter.small)};

  @media (min-width: ${theme.breakpoint.small}px) {
    padding: 0 ${toRem(theme.layout.gutter.large)};
  }

  &:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: none;
  }
`;

const Content = styled.div`
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  background-color: ${theme.color.neutral[50]};
  width: 100%;
  height: auto;
  border-radius: ${toRem(theme.borderRadius.small)};
  border: ${toRem(1)} solid ${theme.color.neutral[200]};
  padding: ${toRem(18)};
`;

const HeaderSection = styled.section`
  flex-grow: 1;
  flex-shrink: 1;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  width: 100%;

  ${H3} {
    display: flex;
    flex-flow: row nowrap;
    justify-content: flex-start;
    align-items: center;
    width: 100%;
    line-height: 110%;
  }
`;

const TitleContainer = styled.div`
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: auto;

  ${H4} {
    margin-top: ${toRem(3.75)};
    color: ${theme.color.neutral[800]};
  }

  .react-loading-skeleton {
    margin: ${toRem(1)} 0;
  }
`;

const Actions = styled.section`
  display: flex;
  flex-flow: row wrap;
  justify-content: flex-end;
  align-items: center;
  margin-left: ${toRem(12)};
  width: 100%;

  @media (max-width: ${theme.breakpoint.med}px) {
    width: auto;
    justify-content: center;
  }

  p {
    font-weight: ${theme.font.weight.medium};
    margin-right: ${toRem(11)};
  }

  button {
    margin-left: ${toRem(24)};

    @media (max-width: ${theme.breakpoint.med}px) {
      margin-top: ${toRem(10)};
      margin-left: 0;
    }

    &:disabled {
      background-color: ${theme.color.neutral[100]};
      color: ${theme.color.neutral[400]};
      border: none;
    }
  }
`;

const StyledSkeleton = styled(Skeleton)`
  margin-right: ${toRem(8)};
`;

const TipAcceptanceEmployee = ({ employee, currentRole, siteId, isLoading, isUpdateLoading, onChangeAcceptance, onViewCode }: ITipAcceptanceEmployeeProps) => {
  const userSite = siteId && employee?.sites.find((site) => site.id === siteId);

  const [recentlyChecked, setRecentlyChecked] = useState(false);

  useEffect(() => {
    if (!isUpdateLoading) {
      setRecentlyChecked(false);
    }
  }, [isUpdateLoading]);

  const handleChange = (value: boolean) => {
    if (value) {
      setRecentlyChecked(true);
    }

    onChangeAcceptance(value);
  };

  return (
    <Layout data-cy="acceptance-employee">
      <Content>
        {isLoading ? (
          <>
            <HeaderSection>
              <StyledSkeleton circle width={33} height={33} />
              <TitleContainer>
                <Skeleton width={100} />
                <Skeleton width={100} />
              </TitleContainer>
            </HeaderSection>
            <Actions>
              <StyledSkeleton width={100} />
              <Skeleton width={100} />
            </Actions>
          </>
        ) : (
          <>
            <HeaderSection>
              <TitleContainer>{employee && <EmployeeHeader employee={employee} userSite={userSite} />}</TitleContainer>
            </HeaderSection>
            <Actions>
              {userSite?.acceptance && (
                <>
                  {recentlyChecked ? (
                    <Skeleton width={60} />
                  ) : (
                    <Text size="small" data-cy="acceptance-status">
                      {userSite.acceptance.enabled ? 'Enabled' : 'Disabled'}
                    </Text>
                  )}
                  <Switch
                    checked={recentlyChecked || userSite.acceptance.enabled}
                    onChange={() => handleChange(!userSite.acceptance.enabled)}
                    disabled={currentRole === SiteRole.MANAGER}
                    data-tooltip-id={`${employee?.id}_manager_disabled`}
                  />
                  {currentRole === SiteRole.MANAGER && <Tooltip id={`${employee?.id}_manager_disabled`}>You do not have permission to manage tip acceptance for this user</Tooltip>}
                </>
              )}
              <Button appearance="white" type="button" size="small" label="View QR Code" onClick={onViewCode} disabled={!userSite?.acceptance?.code} data-cy="view-code-button" />
            </Actions>
          </>
        )}
      </Content>
    </Layout>
  );
};

const HeaderWrapper = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: space-between;

  div.avatar {
    margin-right: ${toRem(6)};
  }
`;

const StyledHeader = styled.div`
  display: flex;
  flex-flow: column nowrap;

  p {
    color: ${theme.color.neutral[500]};
  }
`;

const Title = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  word-break: break-word;

  h4 {
    font-size: ${toRem(14)};
    font-weight: ${theme.font.weight.semiBold};
    margin-right: ${toRem(8)};
  }
`;

interface IEmployeeHeaderProps {
  employee: IEmployee;
  userSite?: IEmployeeSite;
}

const EmployeeHeader = ({ employee, userSite }: IEmployeeHeaderProps) => (
  <HeaderWrapper>
    <Avatar size="medium" image={employee.user?.avatarUrl} id={employee?.id} username={employee.name || ''} hasPaymentMethod={!!employee.user?.card} isDismissed={false} isLoading={false} className="avatar" />
    <StyledHeader>
      <Title>
        <H4>{employee.name}</H4>
      </Title>
      {userSite?.job && <Text size="small">{formatJobTitle(userSite.job.title)}</Text>}
    </StyledHeader>
  </HeaderWrapper>
);

export default TipAcceptanceEmployee;
